import Dagre from '@dagrejs/dagre';
import { Edge, MarkerType, Node } from 'reactflow';
import { AiSmartReplyPlanItemsPrompt } from '../sic-lib/types/Ai';

const getLayoutedElements = (nodes: Node[], edges: Edge[]) => {
    const g = new Dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
    g.setGraph({ rankdir: 'TB' });

    edges.forEach(edge => g.setEdge(edge.source, edge.target));
    nodes.forEach(node =>
        g.setNode(node.id, {
            ...node,
            width: 200,
            height: 100,
        }),
    );

    Dagre.layout(g);

    return {
        nodes: nodes.map(node => {
            const position = g.node(node.id);
            // We are shifting the dagre node position (anchor=center center) to the top left
            // so it matches the React Flow node anchor point (top left).
            const x = position.x - 200 / 2;
            const y = position.y - 100 / 2;

            return { ...node, position: { x, y } };
        }),
        edges,
    };
};

const getPositionedElements = (nodes: Node[], edges: Edge[]): { nodes: Node[]; edges: Edge[] } => {
    const nodeMap = new Map<string, Node>();
    nodes.forEach(node => nodeMap.set(node.id, { ...node, position: { x: 0, y: 0 } }));

    // Step 1: Identify root nodes
    const rootNodes = nodes.filter(node => !edges.some(edge => edge.target === node.id));

    // Step 2: Assign levels to nodes
    let level = 0;
    let currentLevelNodes = rootNodes;
    while (currentLevelNodes.length > 0) {
        const nextLevelNodes: Node[] = [];
        currentLevelNodes.forEach(node => {
            node.position.y = level * 100; // Adjust vertical spacing
            edges.forEach(edge => {
                if (edge.source === node.id) {
                    const targetNode = nodeMap.get(edge.target);
                    if (targetNode && !nextLevelNodes.includes(targetNode)) {
                        nextLevelNodes.push(targetNode);
                    }
                }
            });
        });
        // Step 3: Position nodes horizontally
        currentLevelNodes.forEach((node, index) => {
            node.position.x = index * 200 - (currentLevelNodes.length - 1) * 100; // Adjust horizontal spacing
        });
        level++;
        currentLevelNodes = nextLevelNodes;
    }

    return { nodes: Array.from(nodeMap.values()), edges };
};

export const convertPlanItemsToGraph = (
    items: AiSmartReplyPlanItemsPrompt[],
): { nodes: Node[]; edges: Edge[] } => {
    const nodes: Node[] = items.map(item => ({
        id: item.planItemId.toString(),
        type: item.parentPlanItemId === undefined ? 'input' : 'default',
        data: { label: item.title, planItem: item },
        position: { x: 0, y: 0 },
    }));

    //if the last item has the repeats flag, don't add the newPlanItem placeholder
    const lastItem = items.at(-1);

    if (!lastItem || (lastItem && !lastItem.repeats)) {
        // Add placeholder for new plan item
        nodes.push({
            id: 'newPlanItem',
            type: 'output',
            data: { label: 'Add Plan Item' },
            position: { x: 0, y: 0 },
            style: {
                borderStyle: 'dashed',
                borderColor: '#bbb',
                color: '#bbb',
            },
        });
    }

    const edges: Edge[] = items
        .filter(item => item.parentPlanItemId !== null)
        .map(
            item =>
                ({
                    id: `e${item.parentPlanItemId}-${item.planItemId}`,
                    source: item.parentPlanItemId?.toString(),
                    target: item.planItemId.toString(),
                    animated: true,
                    markerEnd: {
                        type: MarkerType.ArrowClosed,
                    },
                } as Edge),
        );

    // Add edge from last item to newPlanItem placeholder
    if (lastItem) {
        edges.push({
            id: 'newPlanItemEdge',
            source: lastItem.planItemId.toString(),
            target: 'newPlanItem',
            animated: true,
            markerEnd: {
                type: MarkerType.ArrowClosed,
            },
        });
    }

    if (nodes.length === 0 || edges.length === 0) return { nodes, edges };

    return getPositionedElements(nodes, edges);
};
